import {
  getNewBundleId,
  getNewPageId,
} from '@/modules/presentation/clientPresentation/ClientPresentationDesigner.utils';
import {
  buildPageItems,
  BundlePageItem,
} from '@/modules/presentation/components/BundlePageReorderList';
import { PresentationBundleKind, PresentationPageKind } from '@/types/schema';

import { getCarriedOverBundleFields } from '../../../PresentationTemplateEditor.bundleFields';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorPage,
} from '../../../PresentationTemplateEditor.types';

type BeneficiaryConfigKey =
  | 'includeHouseholdOverview'
  | 'includeIndividualOverviews';

export interface BeneficiaryPageItem extends BundlePageItem {
  configKey: BeneficiaryConfigKey;
}

interface BeneficiaryPageDefinition {
  id: string;
  pageKind: PresentationPageKind;
  configKey: BeneficiaryConfigKey;
  label: string;
}

/** The 2 beneficiary page types in their default order. */
export const BENEFICIARY_PAGE_DEFINITIONS: BeneficiaryPageDefinition[] = [
  {
    id: 'householdOverview',
    pageKind: PresentationPageKind.BeneficiaryReportHousehold,
    configKey: 'includeHouseholdOverview',
    label: 'All beneficiaries view',
  },
  {
    id: 'individualBeneficiaries',
    pageKind: PresentationPageKind.BeneficiaryReportBeneficiary,
    configKey: 'includeIndividualOverviews',
    label: 'Select individual beneficiaries',
  },
];

export interface BeneficiaryBundleConfigFormShape {
  pages: BeneficiaryPageItem[];
}

export function getDefaultBeneficiaryPages(
  bundle?: PresentationTemplateEditorBundle
): BeneficiaryPageItem[] {
  if (!bundle) {
    // Create mode: all checked, default order
    return BENEFICIARY_PAGE_DEFINITIONS.map((def) => ({
      ...def,
      checked: true,
    }));
  }

  return buildPageItems<BeneficiaryPageItem>(
    BENEFICIARY_PAGE_DEFINITIONS,
    bundle.beneficiariesTemplateBundleConfiguration,
    bundle.pages.map((p) => p.kind)
  );
}

/**
 * Builds a PresentationTemplateEditorBundle from the modal's form values.
 */
export function buildBeneficiaryBundleFromForm(
  pages: BeneficiaryPageItem[],
  existingBundle?: PresentationTemplateEditorBundle
): PresentationTemplateEditorBundle {
  const checkedPages = pages.filter((p) => p.checked);

  const bundlePages: PresentationTemplateEditorPage[] = checkedPages.map(
    (item) => ({
      id: getNewPageId(item.pageKind),
      kind: item.pageKind,
    })
  );

  // Build the config flags from checked state
  const configFlags: Record<BeneficiaryPageItem['configKey'], boolean> = {
    includeHouseholdOverview: false,
    includeIndividualOverviews: false,
  };

  for (const page of pages) {
    configFlags[page.configKey] = page.checked;
  }

  return {
    ...getCarriedOverBundleFields(existingBundle),
    id:
      existingBundle?.id ??
      getNewBundleId(PresentationBundleKind.BeneficiaryReportBundle),
    kind: PresentationBundleKind.BeneficiaryReportBundle,
    beneficiariesTemplateBundleConfiguration: {
      __typename: 'BeneficiariesTemplateBundleConfiguration' as const,
      id: '',
      ...configFlags,
    },
    pages: bundlePages,
  };
}
